<template>
  <div class="p-4 bg-white rounded-lg h-100 custom-container-height">
    <div class="mb-10 mx-4 mt-6 d-flex align-center justify-space-between">
      <datatable-detail
        class="col-12 col-sm-12 col-md-9"
        :table_properties="reportStructure.properties"
      ></datatable-detail>
      <div v-if="hasExport" class="me-4">
        <button @click="downloadItem" class="btn btn--export-filter">
          <span class="svg-icon">
            <v-icon size="18">mdi-export-variant</v-icon>
          </span>
          {{ $t("export") }}
        </button>
      </div>
    </div>

    <div class="mx-2 mb-6">
      <Filters
        ref="filter"
        :submitFilterValues="submitFilterValues"
        :resetFilterValues="resetFilterValues"
        :filters="filter"
        :setTableFiltersValues="setTableFiltersValues"
      ></Filters>
    </div>
    <div v-if="loading" class="px-3">
      <v-skeleton-loader type="table"></v-skeleton-loader>
    </div>
    <template v-else-if="!loading && chartData">
      <hr class="py-10" />

      <div class="ma-4 pa-4 pt-0 mt-10">
        <div class="row">
          <div
            v-for="(value, index) in chartData"
            :key="index"
            class="col-md-6"
          >
            <v-card>
              <v-card-title class="font-weight-bold">
                {{ translateChartTitle(value.title) }}
              </v-card-title>
              <v-card-subtitle class="text-dark-50">
                {{ $t("forecast_vs_actual") }}
              </v-card-subtitle>
              <v-card-text class="d-block ma-0 pa-0 position-relative">
                <apexchart
                  type="bar"
                  height="300"
                  :options="chartOptions"
                  :series="[
                    {
                      name: $t('forecast'),
                      data: value.forecast,
                    },
                    {
                      name: $t('actual'),
                      data: value.actual,
                    },
                  ]"
                ></apexchart>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";
import Filters from "@/own/components/filter/Filters.vue";
import DatatableDetail from "@/own/components/datatable/DatatableDetail";
import Swal from "sweetalert2";
import ApiService from "@/core/services/api.service";

export default {
  name: "ReportForecastComponent",
  props: {
    /**
     * @description urls to fetch chart data
     * @type {{data_url: string, show_url: string}}
     */
    urls: {
      required: true,
      type: Object,
      default: () => ({ data_url: String, show_url: String }),
    },
    /**
     * @description for setting filters mandatory status
     * @values true, false, ['some_filter_name']
     * @type {boolean, [{title: string, name: string, valueFieldName: string}]}
     */
    mandatoryFilter: {
      type: [Boolean, Array],
      default: false,
    },
    showTableIfClient: {
      default: false,
    },
  },
  components: {
    Filters,
    DatatableDetail,
  },
  beforeMount() {
    this.getReportStructure().then(() => {
      if (this.showTableIfClient) {
        this.getReportData([
          {
            name: "client",
            value: [this.customerId],
          },
        ]);
      }
    });
  },
  data: () => ({
    hasExport: false,
    reportFilters: [
      {
        name: null,
        title: null,
        icon: null,
        type: null,
        related_columns: [],
        values: [],
      },
    ],
    filtersData: [],
    table_state: {
      page: 1,
      per_page: 50,
    },
    reportStructure: {
      properties: {
        title: "",
        description: "",
      },
      filters: [
        {
          name: "",
          title: "",
          type: "",
          related_columns: [],
          values: [
            {
              index: "",
              text: "",
            },
          ],
        },
      ],
      export: true,
    },
    reportData: null,
    loading: false,
    // serverData: {
    //   values: {
    //     categories: ["Q1", "Q2", "Q3", "Q4"],
    //   },
    // },
    // series: [
    //   {
    //     name: "Forecast",
    //     data: [300, 280, 290, 290],
    //   },
    //   {
    //     name: "Actual",
    //     data: [500, 240, 295, 295],
    //   },
    // ],
    chartData: null,
    chartType: null,
    chartLabels: [],
  }),
  computed: {
    filter: function () {
      return this.reportStructure.filters;
    },
    customerId: function () {
      return this.$store.getters.currentUser.data.customer_id;
    },
    chartOptions: function () {
      const rtl = this.$vuetify && this.$vuetify.rtl;
      return {
        chart: {
          type: "bar",
          height: 350,
          toolbar: { show: true },
          ...(rtl ? { rtl: true } : {}),
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            borderRadius: 5,
            borderRadiusApplication: "end",
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: this.chartLabels,
        },
        yaxis: {
          title: {
            // text: "$ (thousands)",
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          // y: {
          //   formatter: function (val) {
          //     return "$ " + val + " thousands";
          //   },
          // },
        },
        legend: {
          show: true,
          position: "top",
        },
        colors: ["#7322B0", "#BE2B82", "#41B883"],
      };
    },
  },
  methods: {
    normalizeApiLabel(label) {
      return String(label || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    },
    translateChartTitle(label) {
      if (label == null || label === "") return label;
      const asString = String(label);
      const key = this.normalizeApiLabel(asString);
      if (key && this.$te(key)) {
        return this.$t(key);
      }
      return asString;
    },
    updateTableState(resetPage = false) {
      if (resetPage) {
        this.table_state.page = 1;
      }
      this.getReportData();
    },
    getReportStructure() {
      this.$store.commit(SET_PAGE_LOADING, true);
      return ApiService.post(this.urls.data_url)
        .then(({ data }) => {
          this.reportStructure = data.data;
          this.hasExport = data.data?.export;
        })
        .finally(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });
    },

    getReportData(customFilter = []) {
      // this.$store.commit(SET_PAGE_LOADING, true);
      this.loading = true;

      if (this.customerId) {
        this.filtersData = [
          ...this.filtersData,
          {
            name: "client",
            value: [this.customerId],
          },
        ];
      }

      const payload = {
        filters: customFilter.length ? customFilter : this.filtersData,
        ...this.table_state,
      };
      ApiService.post(this.urls.show_url, payload)
        .then(({ data }) => {
          const { labels, ...rest } = data.charts[0].values;
          this.chartData = rest;
          this.chartType = data.charts[0].type;
          this.chartLabels = labels;
        })
        .catch()
        .finally(() => {
          // this.$store.commit(SET_PAGE_LOADING, false);
          this.loading = false;
        });
    },
    updateTableContent() {
      this.$forceUpdate();
    },
    /**
     * validate requirement of filters
     * @returns Boolean
     */
    validateFilterValues() {
      let is_valid = true;
      // if mandatoryFilter array then checks are all filters located in array are selected
      if (Array.isArray(this.mandatoryFilter)) {
        this.mandatoryFilter.forEach((filter) => {
          let is_exist =
            this.filter.filter((fil) => fil.name == filter.name).length > 0
              ? false
              : true;
          this.filtersData.forEach((data) => {
            if (data.name === filter.name + filter.valueFieldName) {
              if (
                data.value !== "" &&
                data.value !== null &&
                data.value !== undefined
              ) {
                is_exist = true;
              }
            }
          });
          if (!is_exist) {
            is_valid = false;
          }
        });
      }
      // if mandatoryFilter is true then check all filter values are selected
      else if (this.mandatoryFilter) {
        this.filter.forEach((filter) => {
          let is_exist = false;
          this.filtersData.forEach((data) => {
            if (data.name === filter.name) {
              is_exist = true;
            }
          });
          if (!is_exist) {
            is_valid = false;
          }
        });
      }
      return is_valid;
    },
    submitFilterValues() {
      if (this.filtersData && this.validateFilterValues()) {
        this.getReportData();
      } else {
        if (Array.isArray(this.mandatoryFilter)) {
          let message = this.mandatoryFilter
            .map((filter) => filter.title)
            .join(",");
          const filterLabel = this.$t("filter").toLowerCase();
          Swal.fire(
            this.$t("warning"),
            `${this.$t("select")} ${message} ${filterLabel}${
              this.mandatoryFilter.length > 1 ? "s" : ""
            }!`,
            "warning"
          );
        } else if (this.mandatoryFilter) {
          Swal.fire(
            this.$t("warning"),
            this.$t("select_all_filters"),
            "warning"
          );
        }
      }
    },
    setTableFiltersValues(val) {
      this.filtersData = val;
    },
    resetFilterValues() {
      this.$store.commit(SET_PAGE_LOADING, true);
      this.$refs.filter.$refs.filter.forEach((filter) => {
        filter.clearData();
      });
      this.filtersData = [];
      this.reportData = null;
      this.$store.commit(SET_PAGE_LOADING, false);
    },
    downloadItem() {
      if (this.validateFilterValues()) {
        this.$store.commit(SET_PAGE_LOADING, true);
        const payload = {
          filters: this.filtersData,
        };
        ApiService.post(`${this.urls.data_url}/export`, payload)
          .then(({ data }) => {
            const link = document.createElement("a");
            link.href = data.url;
            link.download = this.$t("export_csv_file_name");
            link.click();
            setTimeout(() => {
              this.$store.commit(SET_PAGE_LOADING, false);
            }, 1500);
            this.$refs.export.toggleModal();
            link.remove();
          })
          .catch(() => {
            this.$store.commit(SET_PAGE_LOADING, false);
          });
      } else
        Swal.fire({
          title: this.$t("warning"),
          text: this.$t("please_select_at_least_one_filter"),
          icon: "warning",
          showConfirmButton: false,
          timer: 2500,
        });
    },
  },
  // watch: {
  //   table_state: {
  //     deep: true,
  //     handler: function () {
  //       this.getReportData();
  //     },
  //   },
  // },
};
</script>

<style scoped>
.custom-container-height {
  overflow-y: scroll;
  position: relative;
  height: calc(100vh - 171px) !important;
}

@media (max-width: 1199.98px) {
  .custom-container-height {
    height: calc(100vh - 141px) !important;
  }
}

@media (max-width: 991.98px) {
  .custom-container-height {
    height: calc(100vh - 126px) !important;
  }
}
</style>
